import { useMemo, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, currentTokens, embedImageUrl, keys } from "@shared/api";
import { links } from "@shared/lib/markup";
import { readMessageLink } from "@shared/lib/jump";
import { IconClose, IconPlay } from "@shared/ui/icons";

/**
 * Карточки ссылок под сообщением — как в Discord: сайт, заголовок, описание и
 * полоса цвета сайта. Страницу смотрит сервер, клиент спрашивает его, а не
 * сайт: так сайт не узнаёт, кто и когда читает переписку.
 *
 * Превью не меняется, пока открыто приложение, и спрашивается один раз на
 * ссылку, сколько бы раз её ни присылали.
 */
/**
 * `onRemove` — крестик на карточке, как в Discord: скрывает превью у всего
 * сообщения. Есть у автора и у тех, кто управляет сообщениями.
 */
export function LinkEmbeds({ content, onRemove }: { content: string; onRemove?: () => void }) {
  // Ссылка на сообщение здесь — уже плашка в тексте, карточка сайта к ней ничего не добавит.
  const urls = useMemo(() => links(content).filter((url) => !readMessageLink(url)), [content]);
  if (urls.length === 0) return null;

  return (
    <div className="embeds">
      {urls.map((url) => (
        <Embed key={url} url={url} onRemove={onRemove} />
      ))}
    </div>
  );
}

function Embed({ url, onRemove }: { url: string; onRemove?: () => void }) {
  const { data } = useQuery({
    queryKey: keys.embed(url),
    queryFn: () => api.embeds.get(url),
    staleTime: Infinity,
    // Не ответил сайт — не ответит и через секунду, а лента не должна стучаться к нему.
    retry: false,
  });
  if (!data) return null;
  const video = youtubeId(url);

  return (
    <div
      className="embed"
      style={data.color ? ({ "--embed-color": data.color } as CSSProperties) : undefined}
    >
      {onRemove && (
        <button type="button" className="embed-remove" aria-label="Убрать превью" title="Убрать превью" onClick={onRemove}>
          <IconClose size={12} />
        </button>
      )}
      {data.site_name && <div className="embed-site ellipsis">{data.site_name}</div>}
      {data.title && (
        <a className="embed-title" href={data.url} target="_blank" rel="noreferrer noopener">
          {data.title}
        </a>
      )}
      {data.description && !video && <div className="embed-text">{data.description}</div>}
      {data.image && video ? (
        <YouTubeEmbed id={video} image={data.image} title={data.title ?? "YouTube"} />
      ) : (
        data.image && <EmbedImage image={data.image} large={!!data.large_image} href={data.url} />
      )}
    </div>
  );
}

/**
 * Номер ролика YouTube из ссылки: watch?v=, youtu.be/, shorts/ и embed/.
 * Чужие адреса и битые номера — `null`, и тогда это обычная карточка.
 */
export function youtubeId(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^(www|m|music)\./, "");
  const id =
    host === "youtu.be"
      ? url.pathname.slice(1)
      : host === "youtube.com" || host === "youtube-nocookie.com"
        ? url.pathname === "/watch"
          ? url.searchParams.get("v")
          : /^\/(?:shorts|embed|live)\/([^/]+)/.exec(url.pathname)?.[1]
        : null;
  return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
}

/**
 * Ролик YouTube — как в Discord: обложка с кнопкой, а по нажатию — плеер прямо
 * в ленте. До нажатия к YouTube не ходим вовсе: плеер грузится только тому,
 * кто захотел смотреть, и с адреса без куки.
 */
function YouTubeEmbed({ id, image, title }: { id: string; image: string; title: string }) {
  const [playing, setPlaying] = useState(false);
  const src = useEmbedImage(image);

  if (playing) {
    return (
      <div className="embed-video">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          // Плееру — его скрипты и окна наружу, но не наша страница.
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="embed-video"
      aria-label={`Смотреть: ${title}`}
      onClick={() => {
        /*
         * Собранное приложение открывает страницу с file://, а без адреса
         * источника YouTube свой плеер не запускает — там ролик открывается
         * на самом YouTube.
         */
        if (window.location.protocol === "file:") {
          window.open(`https://www.youtube.com/watch?v=${id}`, "_blank", "noopener,noreferrer");
          return;
        }
        setPlaying(true);
      }}
    >
      {src && <img src={src} alt="" loading="lazy" />}
      <span className="embed-video-play" aria-hidden>
        <IconPlay size={26} />
      </span>
    </button>
  );
}

/**
 * Картинка карточки — как в Discord: большая под текстом или значок справа,
 * смотря что просит сайт. Берётся через наш сервер и по токену, как вложения:
 * `<img src>` заголовков не шлёт. Ссылка на объект не отзывается — одна
 * карточка встречается в ленте не раз.
 */
function useEmbedImage(image: string): string | null | undefined {
  const { data } = useQuery({
    queryKey: keys.embedImage(image),
    queryFn: async () => {
      const token = currentTokens().access;
      const response = await fetch(embedImageUrl(image), {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return null;
      return URL.createObjectURL(await response.blob());
    },
    staleTime: Infinity,
    retry: false,
  });
  return data;
}

function EmbedImage({ image, large, href }: { image: string; large: boolean; href: string }) {
  const src = useEmbedImage(image);
  // Маленькую картинку большой не растягиваем — она становится значком, как в Discord.
  const [small, setSmall] = useState(false);
  if (!src) return null;

  return (
    <a
      className={`embed-image ${large && !small ? "large" : "thumb"}`}
      href={href}
      target="_blank"
      rel="noreferrer noopener"
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        onLoad={(event) => setSmall(event.currentTarget.naturalWidth < 400)}
      />
    </a>
  );
}
